import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { BarChart3, TrendingUp, DollarSign, Clock, Users, FileText, CheckCircle, XCircle, Calendar, Target, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Reports = () => {
  const { getAuthHeaders } = useAuth();
  const [revenueData, setRevenueData] = useState(null);
  const [arAging, setArAging] = useState(null);
  const [vendorUtilization, setVendorUtilization] = useState(null);
  const [volumeData, setVolumeData] = useState(null);
  const [forecastData, setForecastData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [dateRange, setDateRange] = useState({
    start_date: new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0]
  });

  const fetchReports = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams(dateRange);
      
      const [revenueRes, arRes, vendorRes, volumeRes, forecastRes] = await Promise.all([
        fetch(`${API_URL}/api/reports/revenue?${params}`, {
          headers: getAuthHeaders(),
          credentials: 'include'
        }),
        fetch(`${API_URL}/api/reports/ar-aging`, {
          headers: getAuthHeaders(),
          credentials: 'include'
        }),
        fetch(`${API_URL}/api/reports/vendor-utilization?${params}`, {
          headers: getAuthHeaders(),
          credentials: 'include'
        }),
        fetch(`${API_URL}/api/reports/work-order-volume?${params}&group_by=day`, {
          headers: getAuthHeaders(),
          credentials: 'include'
        }),
        fetch(`${API_URL}/api/reports/revenue-forecast?months_ahead=3`, {
          headers: getAuthHeaders(),
          credentials: 'include'
        })
      ]);
      
      if (revenueRes.ok) setRevenueData(await revenueRes.json());
      if (arRes.ok) setArAging(await arRes.json());
      if (vendorRes.ok) setVendorUtilization(await vendorRes.json());
      if (volumeRes.ok) setVolumeData(await volumeRes.json());
      if (forecastRes.ok) setForecastData(await forecastRes.json());
    } catch (error) {
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
  }, [dateRange]);

  const formatCurrency = (value) => {
    return `$${(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'forecast', label: 'Revenue Forecast', icon: Target },
    { id: 'vendors', label: 'Vendor Utilization', icon: Users },
    { id: 'volume', label: 'Work Order Volume', icon: Calendar },
    { id: 'ar', label: 'AR Aging', icon: Clock }
  ];

  return (
    <div className="space-y-6" data-testid="reports-page">
      {/* Header with Tabs */}
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap gap-2">
          {tabs.map((tab) => (
            <Button
              key={tab.id}
              variant={activeTab === tab.id ? 'default' : 'outline'}
              onClick={() => setActiveTab(tab.id)}
              className="gap-2"
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
            </Button>
          ))}
        </div>
        
        {/* Date Filter */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-wrap gap-4 items-end">
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={dateRange.start_date}
                  onChange={(e) => setDateRange({ ...dateRange, start_date: e.target.value })}
                  data-testid="report-start-date"
                />
              </div>
              <div className="space-y-2">
                <Label>End Date</Label>
                <Input
                  type="date"
                  value={dateRange.end_date}
                  onChange={(e) => setDateRange({ ...dateRange, end_date: e.target.value })}
                  data-testid="report-end-date"
                />
              </div>
              <Button onClick={fetchReports} variant="outline">
                Refresh
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : (
        <>
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Revenue Summary */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">Total Revenue</CardTitle>
                    <DollarSign className="h-4 w-4 text-green-600" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-green-600">
                      {formatCurrency(revenueData?.total_revenue)}
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      {revenueData?.invoice_count || 0} invoices paid
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">Total Cost</CardTitle>
                    <TrendingUp className="h-4 w-4 text-orange-600" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-orange-600">
                      {formatCurrency(revenueData?.total_cost)}
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">Vendor payouts</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">Gross Margin</CardTitle>
                    <BarChart3 className="h-4 w-4 text-primary" />
                  </CardHeader>
                  <CardContent>
                    <div className={`text-3xl font-bold ${revenueData?.margin >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatCurrency(revenueData?.margin)}
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      {revenueData?.margin_percentage || 0}% margin
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">Work Orders</CardTitle>
                    <FileText className="h-4 w-4 text-blue-600" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-blue-600">
                      {(volumeData?.totals?.interpreting || 0) + (volumeData?.totals?.translation || 0)}
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      {volumeData?.totals?.interpreting || 0} INT / {volumeData?.totals?.translation || 0} TRN
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Quick Stats */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Active Vendors</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-4xl font-bold">{vendorUtilization?.summary?.active_vendors || 0}</div>
                    <p className="text-sm text-muted-foreground">
                      {vendorUtilization?.summary?.completion_rate || 0}% completion rate
                    </p>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Assignments Completed</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-4xl font-bold text-green-600">
                      {vendorUtilization?.summary?.total_completed || 0}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      of {vendorUtilization?.summary?.total_assignments || 0} total
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Outstanding AR</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-4xl font-bold text-orange-600">
                      {formatCurrency(
                        (arAging?.current || 0) + 
                        (arAging?.['1_30_days'] || 0) + 
                        (arAging?.['31_60_days'] || 0) + 
                        (arAging?.['61_90_days'] || 0) + 
                        (arAging?.over_90_days || 0)
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">Total receivables</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}

          {/* Revenue Forecast Tab */}
          {activeTab === 'forecast' && (
            <div className="space-y-6">
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card className="border-l-4 border-l-blue-500">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Total Forecast ({forecastData?.summary?.forecast_period_months || 3}mo)</p>
                        <p className="text-2xl font-bold text-blue-600">
                          {formatCurrency(forecastData?.summary?.total_forecast_revenue)}
                        </p>
                      </div>
                      <Target className="h-8 w-8 text-blue-500" />
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-l-4 border-l-green-500">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Scheduled Revenue</p>
                        <p className="text-2xl font-bold text-green-600">
                          {formatCurrency(forecastData?.summary?.total_scheduled_revenue)}
                        </p>
                      </div>
                      <CheckCircle className="h-8 w-8 text-green-500" />
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-l-4 border-l-purple-500">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Translation Pipeline</p>
                        <p className="text-2xl font-bold text-purple-600">
                          {formatCurrency(forecastData?.summary?.translation_pipeline)}
                        </p>
                      </div>
                      <FileText className="h-8 w-8 text-purple-500" />
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-l-4 border-l-orange-500">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Avg Monthly (Historical)</p>
                        <p className="text-2xl font-bold text-orange-600">
                          {formatCurrency(forecastData?.summary?.avg_monthly_historical)}
                        </p>
                      </div>
                      <TrendingUp className="h-8 w-8 text-orange-500" />
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Forecast by Month */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Target className="h-5 w-5" />
                    Monthly Revenue Forecast
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {forecastData?.forecast?.map((month, idx) => (
                      <div key={month.month} className="p-4 rounded-lg bg-muted/30 border">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
                              <Calendar className="h-6 w-6 text-blue-600" />
                            </div>
                            <div>
                              <p className="font-semibold text-lg">{month.month_name}</p>
                              <p className="text-sm text-muted-foreground">
                                {month.scheduled_count} scheduled appointments
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-6">
                            <div className="text-right">
                              <p className="text-sm text-muted-foreground">Scheduled</p>
                              <p className="font-semibold text-green-600">
                                {formatCurrency(month.scheduled_interpreting)}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="text-sm text-muted-foreground">Projected Total</p>
                              <p className="font-bold text-2xl text-blue-600">
                                {formatCurrency(month.projected_total)}
                              </p>
                            </div>
                            <Badge 
                              variant="outline" 
                              className={
                                month.confidence === 'high' 
                                  ? 'bg-green-50 text-green-700 border-green-300' 
                                  : month.confidence === 'medium'
                                    ? 'bg-yellow-50 text-yellow-700 border-yellow-300'
                                    : 'bg-gray-50 text-gray-700 border-gray-300'
                              }
                            >
                              {month.confidence} confidence
                            </Badge>
                          </div>
                        </div>
                      </div>
                    ))}
                    {(!forecastData?.forecast || forecastData.forecast.length === 0) && (
                      <p className="text-center text-muted-foreground py-8">No forecast data available</p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Historical Comparison */}
              {forecastData?.historical && forecastData.historical.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle>Historical Revenue (Last 6 Months)</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                      {forecastData.historical.map((item, idx) => (
                        <div key={item.month} className="p-4 rounded-lg bg-muted/30 text-center">
                          <p className="text-sm text-muted-foreground">{item.month}</p>
                          <p className="text-lg font-bold text-green-600">{formatCurrency(item.actual)}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Insights */}
              <Card>
                <CardHeader>
                  <CardTitle>Forecast Insights</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="p-4 rounded-lg bg-blue-50 border border-blue-200">
                      <div className="flex items-center gap-2 mb-2">
                        <Calendar className="h-4 w-4 text-blue-600" />
                        <span className="font-medium text-blue-800">Scheduled Appointments</span>
                      </div>
                      <p className="text-2xl font-bold text-blue-600">
                        {forecastData?.insights?.scheduled_interpreting_appointments || 0}
                      </p>
                      <p className="text-sm text-blue-700">interpreting sessions in the pipeline</p>
                    </div>
                    <div className="p-4 rounded-lg bg-purple-50 border border-purple-200">
                      <div className="flex items-center gap-2 mb-2">
                        <FileText className="h-4 w-4 text-purple-600" />
                        <span className="font-medium text-purple-800">Translation Projects</span>
                      </div>
                      <p className="text-2xl font-bold text-purple-600">
                        {forecastData?.insights?.translation_projects_in_progress || 0}
                      </p>
                      <p className="text-sm text-purple-700">projects currently in progress</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Vendor Utilization Tab */}
          {activeTab === 'vendors' && (
            <div className="space-y-6">
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-2xl font-bold">{vendorUtilization?.summary?.active_vendors || 0}</div>
                    <p className="text-sm text-muted-foreground">Active Vendors</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-2xl font-bold">{vendorUtilization?.summary?.total_assignments || 0}</div>
                    <p className="text-sm text-muted-foreground">Total Assignments</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-2xl font-bold text-green-600">{vendorUtilization?.summary?.total_completed || 0}</div>
                    <p className="text-sm text-muted-foreground">Completed</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-2xl font-bold">{vendorUtilization?.summary?.completion_rate || 0}%</div>
                    <p className="text-sm text-muted-foreground">Completion Rate</p>
                  </CardContent>
                </Card>
              </div>

              {/* Vendor Table */}
              <Card>
                <CardHeader>
                  <CardTitle>Vendor Performance</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left p-3 font-medium">Vendor</th>
                          <th className="text-center p-3 font-medium">Assignments</th>
                          <th className="text-center p-3 font-medium">Completed</th>
                          <th className="text-center p-3 font-medium">Declined</th>
                          <th className="text-center p-3 font-medium">No-Show</th>
                          <th className="text-center p-3 font-medium">Acceptance Rate</th>
                          <th className="text-right p-3 font-medium">Earnings</th>
                        </tr>
                      </thead>
                      <tbody>
                        {vendorUtilization?.vendors?.map((vendor, idx) => (
                          <tr key={idx} className="border-b hover:bg-muted/50">
                            <td className="p-3 font-medium">{vendor.vendor_name}</td>
                            <td className="p-3 text-center">{vendor.total_assignments}</td>
                            <td className="p-3 text-center">
                              <Badge variant="outline" className="bg-green-50 text-green-700">
                                {vendor.completed}
                              </Badge>
                            </td>
                            <td className="p-3 text-center">
                              {vendor.declined > 0 ? (
                                <Badge variant="outline" className="bg-orange-50 text-orange-700">
                                  {vendor.declined}
                                </Badge>
                              ) : '-'}
                            </td>
                            <td className="p-3 text-center">
                              {vendor.no_show > 0 ? (
                                <Badge variant="outline" className="bg-red-50 text-red-700">
                                  {vendor.no_show}
                                </Badge>
                              ) : '-'}
                            </td>
                            <td className="p-3 text-center">
                              <span className={vendor.acceptance_rate >= 80 ? 'text-green-600' : vendor.acceptance_rate >= 50 ? 'text-yellow-600' : 'text-red-600'}>
                                {vendor.acceptance_rate}%
                              </span>
                            </td>
                            <td className="p-3 text-right font-medium">{formatCurrency(vendor.total_earnings)}</td>
                          </tr>
                        ))}
                        {(!vendorUtilization?.vendors || vendorUtilization.vendors.length === 0) && (
                          <tr>
                            <td colSpan="7" className="p-8 text-center text-muted-foreground">
                              No vendor data available for this period
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Volume Tab */}
          {activeTab === 'volume' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Interpreting</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-5xl font-bold text-blue-600">{volumeData?.totals?.interpreting || 0}</div>
                    <p className="text-muted-foreground mt-2">Total interpreting assignments</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle>Translation</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-5xl font-bold text-purple-600">{volumeData?.totals?.translation || 0}</div>
                    <p className="text-muted-foreground mt-2">Total translation projects</p>
                  </CardContent>
                </Card>
              </div>

              {/* Daily Volume */}
              <Card>
                <CardHeader>
                  <CardTitle>Daily Volume</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {volumeData?.volume?.map((day, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                        <span className="font-medium">{day.date}</span>
                        <div className="flex gap-4">
                          <Badge variant="outline" className="bg-blue-50">
                            INT: {day.interpreting}
                          </Badge>
                          <Badge variant="outline" className="bg-purple-50">
                            TRN: {day.translation}
                          </Badge>
                        </div>
                      </div>
                    ))}
                    {(!volumeData?.volume || volumeData.volume.length === 0) && (
                      <p className="text-center text-muted-foreground py-8">No data for this period</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* AR Aging Tab */}
          {activeTab === 'ar' && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  Accounts Receivable Aging
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div className="p-4 rounded-lg bg-green-50 border border-green-200">
                    <p className="text-sm text-green-800 font-medium">Current</p>
                    <p className="text-2xl font-bold text-green-600">
                      {formatCurrency(arAging?.current)}
                    </p>
                  </div>
                  <div className="p-4 rounded-lg bg-yellow-50 border border-yellow-200">
                    <p className="text-sm text-yellow-800 font-medium">1-30 Days</p>
                    <p className="text-2xl font-bold text-yellow-600">
                      {formatCurrency(arAging?.['1_30_days'])}
                    </p>
                  </div>
                  <div className="p-4 rounded-lg bg-orange-50 border border-orange-200">
                    <p className="text-sm text-orange-800 font-medium">31-60 Days</p>
                    <p className="text-2xl font-bold text-orange-600">
                      {formatCurrency(arAging?.['31_60_days'])}
                    </p>
                  </div>
                  <div className="p-4 rounded-lg bg-red-50 border border-red-200">
                    <p className="text-sm text-red-800 font-medium">61-90 Days</p>
                    <p className="text-2xl font-bold text-red-600">
                      {formatCurrency(arAging?.['61_90_days'])}
                    </p>
                  </div>
                  <div className="p-4 rounded-lg bg-red-100 border border-red-300">
                    <p className="text-sm text-red-800 font-medium">90+ Days</p>
                    <p className="text-2xl font-bold text-red-700">
                      {formatCurrency(arAging?.over_90_days)}
                    </p>
                  </div>
                </div>
                
                <div className="mt-6 p-4 bg-muted/30 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="font-medium">Total Outstanding</span>
                    <span className="text-2xl font-bold">
                      {formatCurrency(
                        (arAging?.current || 0) + 
                        (arAging?.['1_30_days'] || 0) + 
                        (arAging?.['31_60_days'] || 0) + 
                        (arAging?.['61_90_days'] || 0) + 
                        (arAging?.over_90_days || 0)
                      )}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
};

export default Reports;
